import { useState, useEffect, useMemo, useCallback } from "react";
import { useForm } from "react-hook-form";
import Swal from "sweetalert2";
import {
  safeValue,
  hasValue,
  isChecked,
  saveReceptionData,
  validateSums,
  validateFlightSums,
  getMaxValues,
} from "../util";

function ReceptionForm({
  master,
  subproduct,
  parentMaster,
  isConsolidated,
  showForm,
}) {
  // Definir todos los hooks primero, antes de cualquier lógica condicional
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [leftValidation, setLeftValidation] = useState(null);
  const [rightValidation, setRightValidation] = useState(null);
  const [flightSumErrors, setFlightSumErrors] = useState({
    col: null,
    fro: null,
  });
  const [maxValues, setMaxValues] = useState({ pieces: 0, weight: 0 });

  // Datos memoizados para las tablas - definidos al inicio
  const natureRows = useMemo(
    () => [
      { key: "nat_cgo", label: "GENERAL", code: "CGO" },
      { key: "nat_dgr", label: "PELIGROSA", code: "DGR" },
      { key: "nat_per", label: "PERECEDERO", code: "PER" },
      { key: "nat_avi", label: "ANIMAL VIVO", code: "AVI" },
      { key: "nat_val", label: "ALTO VALOR", code: "VAL" },
      { key: "nat_hum", label: "RESTOS HUMANOS", code: "HUM" },
      { key: "nat_dip", label: "VALIJA DIPLOMATICA", code: "DIP" },
      { key: "nat_nwp", label: "PRENSA", code: "NWP" },
      { key: "nat_mal", label: "MAIL", code: "MAL" },
      { key: "nat_com", label: "COMAT AM", code: "COM" },
      { key: "nat_vic", label: "VALOR DECLARADO", code: "VIC" },
      { key: "nat_pil", label: "PHARMA", code: "PIL" },
    ],
    [],
  );

  const handlingRows = useMemo(
    () => [
      { key: "han_ice", label: "HIELO-SECO", code: "ICE" },
      { key: "han_wet", label: "HÚMEDA", code: "WET" },
      { key: "han_big", label: "GRANDE", code: "BIG" },
      { key: "han_hea", label: "HEAVY", code: "HEA" },
      { key: "han_hwc", label: "FRAGIL", code: "HWC" },
      { key: "han_col", label: "REFRI", code: "COL" },
      { key: "han_fro", label: "CONGELADOR", code: "FRO" },
      { key: "han_na", label: "NO APLICA", code: "N/A" },
    ],
    [],
  );

  // Datos memoizados para documentos
  const documentRows = useMemo(
    () => [
      { label: "Guía Master", copies: 4, field: "master_awb", hasEawb: true },
      {
        label: "House Manifest",
        copies: 4,
        field: "house_manifest",
        show: !!subproduct,
      },
      {
        label: "Pedimento Simplificado con sello gama/DODA",
        copies: 4,
        field: "simplified_request",
      },
      {
        label: "Transferencia de carga",
        copies: 2,
        field: "cargo_transfer",
        na: false,
      },
      { label: "Carta de seguridad", copies: 2, field: "security_letter" },
      { label: "Carta de aceptación", copies: 2, field: "acceptance_letter" },
      {
        label: "Copia gafete Aduanal",
        copies: 2,
        field: "custom_badge",
        na: false,
      },
    ],
    [subproduct],
  );

  // Datos memoizados para entregas adicionales
  const additionalDeliveries = useMemo(
    () => [
      {
        label: "CARGAS ESPECIALES",
        field: "specialLoads",
        masterField: "special_cargo",
        tableData: [
          {
            label: "Cuaderno ATA, Oficio SAT",
            copies: 1,
            field: "ata_sat_office",
          },
        ],
      },
      {
        label: "CARGAS INTERLINEALES",
        field: "interlineLoads",
        masterField: "interlineal_cargo",
        tableData: [
          { label: "Transferencia", copies: 1, field: "intcar_transfer" },
        ],
      },
      {
        label: "PERECEDEROS",
        field: "nat_per_disabled",
        watchField: "nat_per",
        tableData: [
          {
            label:
              "Certificado fitosanitario o Certificado de negativa del certificado",
            copies: 1,
            field: "perishables_certified",
            hasNA: true,
          },
        ],
      },
      {
        label: "MERCANCÍAS PELIGROSAS",
        field: "nat_dgr_disabled",
        watchField: "nat_dgr",
        tableData: [{ label: "DGD", copies: 2, field: "dg" }],
      },
      {
        label: "ANIMALES VIVOS",
        field: "nat_avi_disabled",
        watchField: "nat_avi",
        tableData: [
          {
            label: "Certificado del embarcador (no IATA)",
            copies: 1,
            field: "shipper_certified",
          },
        ],
      },
      {
        label: "RESTOS HUMANOS",
        field: "nat_hum_disabled",
        watchField: "nat_hum",
        tableData: [
          { label: "Certificado de Defunción*", copies: 1, field: "hr_cert" },
          {
            label: "Permiso de Traslado*",
            copies: 1,
            field: "hr_permission_transfer",
          },
          {
            label: "Permiso de Embalsamamiento*",
            copies: 1,
            field: "hr_permission",
          },
        ],
        header: "* Incluye apostillado",
      },
    ],
    [],
  );

  const getRadioInitialValue = useCallback(
    (fieldName) => {
      const value = master?.preve_save?.[fieldName];
      if (value == null) return "";
      if ([1, 0, 2].includes(value)) return value.toString();
      return typeof value === "string" ? value : "";
    },
    [master],
  );

  // Configurar valores iniciales
  const defaultValues = useMemo(() => {
    if (!master) return {};
    const preve = master?.preve_save || {};

    const entries = [
      // Documentos a entregar
      ["master_awb", getRadioInitialValue("master_awb")],
      ["master_eawb", preve.master_awb_e ? true : false],
      ["house_manifest", getRadioInitialValue("house_manifest")],
      ["simplified_request", getRadioInitialValue("simplified_request")],
      ["cargo_transfer", getRadioInitialValue("cargo_transfer")],
      ["security_letter", getRadioInitialValue("security_letter")],
      ["acceptance_letter", getRadioInitialValue("acceptance_letter")],
      ["custom_badge", getRadioInitialValue("custom_badge")],

      // Entregas adicionales
      [
        "ata_sat_office",
        preve.ata_sat_office != null ? preve.ata_sat_office.toString() : "",
      ],
      [
        "intcar_transfer",
        preve.intcar_transfer != null ? preve.intcar_transfer.toString() : "",
      ],
      [
        "perishables_certified",
        preve.perishables_certified != null
          ? preve.perishables_certified.toString()
          : "",
      ],
      ["dg", preve.dg != null ? preve.dg.toString() : ""],
      [
        "shipper_certified",
        preve.shipper_certified != null
          ? preve.shipper_certified.toString()
          : "",
      ],
      ["hr_cert", preve.hr_cert != null ? preve.hr_cert.toString() : ""],
      [
        "hr_permission_transfer",
        preve.hr_permission_transfer != null
          ? preve.hr_permission_transfer.toString()
          : "",
      ],
      [
        "hr_permission",
        preve.hr_permission != null ? preve.hr_permission.toString() : "",
      ],

      // DG y temperatura
      ["has_dg_exempt", preve.has_dg_exempt || ""],
      ["un_dg", preve.un_dg || ""],
      ["required_temp", preve.required_temp || "N/A"],
    ];

    // Checkboxes básicos
    const checkboxFields = [
      "nat_cgo",
      "nat_dgr",
      "nat_per",
      "nat_avi",
      "nat_val",
      "nat_hum",
      "nat_dip",
      "nat_nwp",
      "nat_mal",
      "nat_com",
      "nat_vic",
      "nat_pil",
      "han_ice",
      "han_wet",
      "han_big",
      "han_hea",
      "han_hwc",
      "han_col",
      "han_fro",
      "han_na",
      "contActivos_RKN",
      "contActivos_RAP",
      "prepContenedor_RKN",
      "prepContenedor_RAP",
      "specialLoads",
      "interlineLoads",
    ];
    checkboxFields.forEach((key) => entries.push([key, preve[key] || false]));

    // Piezas y peso
    const piecesWeightFields = checkboxFields.slice(0, -4); // Excluir últimos 4
    piecesWeightFields.forEach((key) => {
      entries.push([`${key}_pieces`, preve[`${key}_pieces`] || ""]);
      entries.push([`${key}_weight`, preve[`${key}_weight`] || ""]);
    });

    // Números de contenedores
    [
      "contActivos_RKN",
      "contActivos_RAP",
      "prepContenedor_RKN",
      "prepContenedor_RAP",
    ].forEach((key) =>
      entries.push([`${key}_number`, preve[`${key}_number`] || ""]),
    );

    return Object.fromEntries(entries);
  }, [master, getRadioInitialValue]);

  const { register, handleSubmit, watch, setValue, getValues } = useForm({
    defaultValues,
  });

  const watchAllFields = watch();

  // Memoizar la detección de temperaturas especiales
  const hasTemperatureRequired = useMemo(() => {
    return watchAllFields.han_col || watchAllFields.han_fro;
  }, [watchAllFields.han_col, watchAllFields.han_fro]);

  // Callbacks memoizados
  const onSubmit = useCallback(
    async (formData) => {
      const leftResult = validateSums(formData, master, "left");
      const rightResult = validateSums(formData, master, "right");

      if (!leftResult.isValid || !rightResult.isValid) {
        Swal.fire({
          title: !leftResult.isValid
            ? "Error en sumas de Naturaleza"
            : "Error en sumas de Manejo",
          text: !leftResult.isValid
            ? leftResult.errors.pieces || leftResult.errors.weight
            : rightResult.errors.pieces || rightResult.errors.weight,
          icon: "error",
          confirmButtonText: "Aceptar",
        });
        return;
      }

      if (flightSumErrors.col || flightSumErrors.fro) {
        Swal.fire({
          title: flightSumErrors.col
            ? "Error en vuelos REFRI"
            : "Error en vuelos CONGELADOR",
          text: flightSumErrors.col || flightSumErrors.fro,
          icon: "error",
          confirmButtonText: "Aceptar",
        });
        return;
      }

      setIsSubmitting(true);
      try {
        const userData = localStorage.getItem("user");
        const user = userData ? JSON.parse(userData) : null;
        await saveReceptionData(formData, master, subproduct, user);
      } catch (error) {
        console.error("Error in onSubmit:", error);
      } finally {
        setIsSubmitting(false);
      }
    },
    [master, subproduct, flightSumErrors],
  );

  const handleTicket = useCallback(() => alert("Ticket"), []);

  const renderRadio = useCallback(
    (fieldName, value, label) => (
      <>
        {label}{" "}
        <input
          type="radio"
          name={fieldName}
          value={value}
          checked={watch(fieldName) === value}
          onChange={() => setValue(fieldName, value)}
        />
      </>
    ),
    [watch, setValue],
  );

  const renderValidatedInput = useCallback(
    (field, type = "number", disabled = false) => {
      const isInvalid =
        (field.includes("nat_") && leftValidation?.errors?.pieces) ||
        (field.includes("han_") && rightValidation?.errors?.pieces);
      return (
        <input
          type={type}
          {...register(field)}
          className={`form-control form-control-sm ${isInvalid ? "is-invalid" : ""}`}
          disabled={disabled}
          min="0"
          max={
            type === "number"
              ? field.includes("pieces")
                ? maxValues.pieces
                : maxValues.weight
              : undefined
          }
          step={
            type === "number" && field.includes("weight") ? "0.01" : undefined
          }
        />
      );
    },
    [register, leftValidation, rightValidation, maxValues],
  );

  const renderTableRow = useCallback(
    (nature, handling = null) => (
      <tr className="text-start" key={`${nature.key}-${handling?.key || ""}`}>
        {/* Naturaleza */}
        <td className="text-end">{nature.code}</td>
        <td>
          <input
            type="checkbox"
            {...register(nature.key)}
            defaultChecked={isChecked(master?.preve_save?.[nature.key])}
          />{" "}
          {nature.label}
        </td>
        <td>
          {watch(nature.key) && renderValidatedInput(`${nature.key}_pieces`)}
        </td>
        <td>
          {watch(nature.key) && renderValidatedInput(`${nature.key}_weight`)}
        </td>

        {/* Manejo */}
        {handling ? (
          <>
            <td className="text-end">{handling.code}</td>
            <td>
              <input
                type="checkbox"
                {...register(handling.key)}
                defaultChecked={isChecked(master?.preve_save?.[handling.key])}
              />{" "}
              {handling.label}
            </td>
            <td>
              {watch(handling.key) &&
                renderValidatedInput(`${handling.key}_pieces`)}
            </td>
            <td>
              {watch(handling.key) &&
                renderValidatedInput(`${handling.key}_weight`)}
            </td>
          </>
        ) : (
          <>
            <td />
            <td />
            <td />
            <td />
          </>
        )}
      </tr>
    ),
    [register, watch, renderValidatedInput, master],
  );

  // Efectos optimizados con dependencias específicas
  useEffect(() => {
    const max = getMaxValues(master);
    setMaxValues(max);
  }, [master]);

  // Observar campos de naturaleza - optimizado
  useEffect(() => {
    const watchedValues = getValues();
    const leftResult = validateSums(watchedValues, master, "left");
    setLeftValidation(leftResult);
  }, [
    master,
    getValues,
    ...natureRows.flatMap((row) => [
      watchAllFields[row.key],
      watchAllFields[`${row.key}_pieces`],
      watchAllFields[`${row.key}_weight`],
    ]),
  ]);

  // Observar campos de manejo - optimizado
  useEffect(() => {
    const watchedValues = getValues();
    const rightResult = validateSums(watchedValues, master, "right");
    setRightValidation(rightResult);
  }, [
    master,
    getValues,
    ...handlingRows.flatMap((row) => [
      watchAllFields[row.key],
      watchAllFields[`${row.key}_pieces`],
      watchAllFields[`${row.key}_weight`],
    ]),
  ]);

  // Validar vuelos REFRI y CONGELADOR - optimizado
  useEffect(() => {
    const validateFlight = (type) => {
      if (watchAllFields[`han_${type}`]) {
        const validation = validateFlightSums(getValues(), master, type);
        setFlightSumErrors((prev) => ({
          ...prev,
          [type]: validation && !validation.isValid ? validation.message : null,
        }));
      } else {
        setFlightSumErrors((prev) => ({ ...prev, [type]: null }));
      }
    };

    validateFlight("col");
    validateFlight("fro");
  }, [
    master,
    getValues,
    watchAllFields.han_col,
    watchAllFields.han_fro,
    watchAllFields.han_col_pieces,
    watchAllFields.han_col_weight,
    watchAllFields.han_fro_pieces,
    watchAllFields.han_fro_weight,
  ]);

  // Efecto para controlar el select de temperatura
  useEffect(() => {
    if (hasTemperatureRequired) {
      // Si tiene COL, REFRI o CONGELADOR, asegurar que no sea N/A
      const currentTemp = getValues("required_temp");
      if (currentTemp === "N/A") {
        // Buscar la primera opción que no sea N/A
        const nonNaOption = master?.temperatures?.find(
          (temp) => temp.name !== "N/A",
        );
        if (nonNaOption) {
          setValue("required_temp", nonNaOption.name);
        }
      }
    } else {
      // Si no tiene COL, REFRI o CONGELADOR, forzar N/A
      setValue("required_temp", "N/A");
    }
  }, [hasTemperatureRequired, master?.temperatures, setValue, getValues]);

  // Renderizar tabla consolidada - ESTO ES LO ÚNICO QUE QUEDA DESPUÉS DE LOS HOOKS
  if (isConsolidated && !subproduct && parentMaster?.data?.length > 0) {
    return (
      <div className="mt-5">
        <table className="table" style={{ width: "70%", margin: "0 auto" }}>
          <thead className="text-center">
            <tr>
              <th style={{ backgroundColor: "#020C41", color: "white" }}>
                HBS
              </th>
              <th style={{ backgroundColor: "#020C41", color: "white" }}>
                Fecha
              </th>
              <th style={{ backgroundColor: "#020C41", color: "white" }}>
                Piezas
              </th>
              <th style={{ backgroundColor: "#020C41", color: "white" }}>
                Peso
              </th>
              <th style={{ backgroundColor: "#020C41", color: "white" }}>
                Acciones
              </th>
            </tr>
          </thead>
          <tbody className="text-center">
            {parentMaster?.data?.map((sub, index) => (
              <tr key={index}>
                <td>{safeValue(sub.hbs, "N/A")}</td>
                <td>{safeValue(sub.date, "N/A")}</td>
                <td>{safeValue(sub.pieces, "N/A")}</td>
                <td>{safeValue(sub.weight, "N/A")}</td>
                <td>
                  <button
                    className="btn btn-primary btn-sm"
                    onClick={() => showForm(sub, "reception")}
                  >
                    ➜
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {(!parentMaster?.data || parentMaster.data.length === 0) && (
          <div className="text-center mt-3">
            <p className="text-muted">No hay subproductos disponibles</p>
          </div>
        )}
      </div>
    );
  }

  if (!master) {
    return (
      <div className="container-fluid">
        <div className="alert alert-info text-center">
          Cargando datos del formulario...
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className="container-fluid">
        {/* Información del master */}
        <div className="row">
          {[
            { label: "Master:", value: safeValue(master?.resp?.awb, "N/A") },
            { label: "Origen:", value: safeValue(master?.resp?.master_orig) },
            {
              label: "Destino:",
              value: safeValue(master?.resp?.master_dest, "N/A"),
            },
            {
              label: "Tipo de mercancía:",
              value: safeValue(master?.resp?.master_merch, "N/A"),
            },
          ].map((item, i) => (
            <div key={i} className="col-3">
              <p>
                <span className="fw-bold">{item.label}</span> {item.value}
              </p>
            </div>
          ))}
        </div>

        <div className="row">
          {[
            {
              label: "Piezas Master:",
              value: safeValue(master?.resp?.master_pieces, "N/A"),
            },
            {
              label: "Peso Master:",
              value: safeValue(master?.resp?.master_weight, "N/A"),
            },
            {
              label: "Volúmen Master:",
              value: safeValue(master?.resp?.master_volume, "N/A"),
            },
            subproduct && { label: "HBS:", value: subproduct?.hbs },
          ]
            .filter(Boolean)
            .map((item, i) => (
              <div key={i} className="col-3">
                <p>
                  <span className="fw-bold">{item.label}</span> {item.value}
                </p>
              </div>
            ))}
        </div>

        {subproduct && (
          <div className="row">
            {[
              {
                label: "Piezas House:",
                value: safeValue(subproduct?.piezas, "N/A"),
              },
              {
                label: "Peso House:",
                value: safeValue(subproduct?.peso, "N/A"),
              },
              {
                label: "Volúmen House:",
                value: safeValue(subproduct?.peso, "N/A"),
              },
            ].map((item, i) => (
              <div key={i} className="col-3">
                <p>
                  <span className="fw-bold">{item.label}</span> {item.value}
                </p>
              </div>
            ))}
          </div>
        )}

        {/* Documentos a entregar */}
        <table className="table table-striped mt-3">
          <thead className="text-center">
            <tr>
              <th
                style={{ backgroundColor: "#020C41", color: "white" }}
                colSpan="6"
              >
                Documentos a entregar
              </th>
            </tr>
            <tr>
              <th />
              <th>No. Copias</th>
              <th />
              <th />
              <th />
              <th />
            </tr>
          </thead>
          <tbody className="text-center">
            {documentRows.map(
              (doc, i) =>
                doc.show !== false && (
                  <tr key={i}>
                    <td className="text-start">{doc.label}</td>
                    <td>{doc.copies}</td>
                    <td>{renderRadio(doc.field, "1", "Si")}</td>
                    <td>{renderRadio(doc.field, "0", "No")}</td>
                    <td>
                      {doc.na !== false && renderRadio(doc.field, "2", "N/A")}
                    </td>
                    <td>
                      {doc.hasEawb && (
                        <>
                          ¿e-AWB? (Guía Electrónica){" "}
                          <input type="checkbox" {...register("master_eawb")} />
                        </>
                      )}
                    </td>
                  </tr>
                ),
            )}
          </tbody>
        </table>

        {/* Tabla principal de naturaleza y manejo */}
        <table className="table table-striped mt-4">
          <colgroup>
            <col style={{ width: "10%" }} />
            <col style={{ width: "20%" }} />
            <col style={{ width: "10%" }} />
            <col style={{ width: "10%" }} />
            <col style={{ width: "10%" }} />
            <col style={{ width: "20%" }} />
            <col style={{ width: "10%" }} />
            <col style={{ width: "10%" }} />
          </colgroup>
          <thead className="text-center">
            <tr>
              <th style={{ backgroundColor: "#020C41", color: "white" }}>
                NATURALEZA
              </th>
              <th style={{ backgroundColor: "#020C41", color: "white" }}></th>
              <th style={{ backgroundColor: "#020C41", color: "white" }}>
                Piezas
              </th>
              <th style={{ backgroundColor: "#020C41", color: "white" }}>
                Peso
              </th>
              <th style={{ backgroundColor: "#020C41", color: "white" }}>
                MANEJO
              </th>
              <th style={{ backgroundColor: "#020C41", color: "white" }}>
                MANEJO
              </th>
              <th style={{ backgroundColor: "#020C41", color: "white" }}>
                Piezas
              </th>
              <th style={{ backgroundColor: "#020C41", color: "white" }}>
                Peso
              </th>
            </tr>
          </thead>
          <tbody className="text-center">
            {natureRows.map((nature, i) =>
              renderTableRow(nature, handlingRows[i] || null),
            )}
          </tbody>
        </table>

        {/* Temperatura y ACAS */}
        <div className="row">
          <div className="col-5">
            <p>
              <span className="fw-bold">DESTINO ACAS:</span>&nbsp;{" "}
              {master?.acas_iatas ? "SI" : "NO"}
            </p>
          </div>
          <div className="col-6 d-flex align-items-center gap-2">
            <span className="fw-bold">*Temperatura requerida:</span>
            <select
              className="form-select"
              style={{ width: "auto" }}
              {...register("required_temp")}
              disabled={!hasTemperatureRequired}
            >
              {master?.temperatures?.length > 0 ? (
                master.temperatures.map((temp) => (
                  <option
                    key={temp.id}
                    value={safeValue(temp.name, "N/A")}
                    disabled={hasTemperatureRequired && temp.name === "N/A"}
                  >
                    {safeValue(temp.name, "N/A")}
                  </option>
                ))
              ) : (
                <option value="N/A">N/A</option>
              )}
            </select>
          </div>
        </div>

        {/* Contenedores activos */}
        {safeValue(master?.merchandise_type?.name) === "Cont activos" && (
          <div className="row align-items-center mb-3">
            {[
              { title: "Contenedores activos", prefix: "contActivos" },
              { title: "Preparación de contenedor", prefix: "prepContenedor" },
            ].map((section, idx) => (
              <div key={idx} className="col-4 d-flex flex-column gap-3">
                <p
                  className="sub-title fw-bold mt-4"
                  style={{ color: "#020C41" }}
                >
                  {section.title}
                </p>
                {["RKN", "RAP"].map((type) => {
                  const key = `${section.prefix}_${type}`;
                  return (
                    <div key={type} className="d-flex align-items-center gap-2">
                      <input
                        type="checkbox"
                        {...register(key)}
                        defaultChecked={isChecked(master?.preve_save?.[key])}
                        onChange={(e) =>
                          !e.target.checked && setValue(`${key}_number`, "")
                        }
                      />
                      <span>{type}</span>
                      <input
                        type="number"
                        className="form-control form-control-sm w-50"
                        {...register(`${key}_number`)}
                        disabled={!watch(key)}
                      />
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        )}

        {/* Vuelos REFRI y CONGELADOR */}
        {["col", "fro"].map(
          (type) =>
            watch(`han_${type}`) &&
            master?.booking_flights?.length > 0 && (
              <div key={type}>
                <p
                  className="sub-title fw-bold mt-4"
                  style={{ color: "#020C41" }}
                >
                  Vuelos Reservados {type === "col" ? "Refri" : "Conge"}
                </p>
                {flightSumErrors[type] && (
                  <div className="alert alert-danger">
                    {flightSumErrors[type]}
                  </div>
                )}
                <table className="table mt-3">
                  <thead className="text-center">
                    <tr>
                      {["Vuelo", "Año", "Hora", "Piezas", "Peso"].map(
                        (header) => (
                          <th
                            key={header}
                            style={{
                              backgroundColor: "#020C41",
                              color: "white",
                            }}
                          >
                            {header}
                          </th>
                        ),
                      )}
                    </tr>
                  </thead>
                  <tbody className="text-center">
                    {master.booking_flights.map((flight, i) => (
                      <tr key={i}>
                        <td>{safeValue(flight.flight, "N/A")}</td>
                        <td>{safeValue(flight.year, "N/A")}</td>
                        <td>{safeValue(flight.time, "N/A")}</td>
                        <td>
                          <input
                            type="number"
                            {...register(`flights_${type}_pieces_${i}`)}
                            defaultValue={safeValue(
                              flight[`${type}_pieces`],
                              "",
                            )}
                            className={`form-control form-control-sm ${flightSumErrors[type] ? "is-invalid" : ""}`}
                            min="0"
                            max={
                              watchAllFields[`han_${type}_pieces`] ||
                              maxValues.pieces
                            }
                          />
                        </td>
                        <td>
                          <input
                            type="number"
                            {...register(`flights_${type}_weight_${i}`)}
                            defaultValue={safeValue(
                              flight[`${type}_weight`],
                              "",
                            )}
                            className={`form-control form-control-sm ${flightSumErrors[type] ? "is-invalid" : ""}`}
                            min="0"
                            step="0.01"
                            max={
                              watchAllFields[`han_${type}_weight`] ||
                              maxValues.weight
                            }
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ),
        )}

        {/* DG */}
        {watch("nat_dgr") && (
          <>
            <p className="amc-text fw-bold mt-4" style={{ color: "#020C41" }}>
              ¿Contiene DG exentas de verificación?
            </p>
            {["SÍ", "NO"].map((label, i) => (
              <span key={label}>
                {label}{" "}
                <input
                  type="radio"
                  {...register("has_dg_exempt")}
                  value={i === 0 ? "si" : "no"}
                  defaultChecked={
                    safeValue(master?.preve_save?.has_dg_exempt) ===
                    (i === 0 ? "si" : "no")
                  }
                  style={{ marginRight: i === 0 ? "14rem" : "" }}
                />
              </span>
            ))}
            {watch("has_dg_exempt") === "no" && (
              <div style={{ marginTop: "1rem" }}>
                <p
                  className="amc-text fw-bold mt-4"
                  style={{ color: "#020C41" }}
                >
                  UN (MERCANCÍAS PELIGROSAS)
                </p>
                <input
                  type="text"
                  {...register("un_dg")}
                  className="form-control mb-2"
                  style={{ width: "20%" }}
                />
                <p className="amc-notes" style={{ color: "#DC3545" }}>
                  Agregar cantidad total de UN en la guía
                </p>
              </div>
            )}
          </>
        )}

        {/* Entregas adicionales */}
        <div className="row">
          <div className="col">
            <p className="sub-title fw-bold mt-4" style={{ color: "#020C41" }}>
              Entregas adicionales
            </p>

            {additionalDeliveries.map((delivery, idx) => (
              <div key={idx}>
                <p>
                  <input
                    type="checkbox"
                    {...register(delivery.field)}
                    checked={
                      delivery.watchField
                        ? watch(delivery.watchField)
                        : watch(delivery.field)
                    }
                    disabled={!!delivery.watchField}
                    defaultChecked={
                      delivery.masterField
                        ? hasValue(master?.preve_save?.[delivery.masterField])
                        : false
                    }
                  />{" "}
                  {delivery.label}
                </p>

                {(delivery.watchField
                  ? watch(delivery.watchField)
                  : watch(delivery.field)) && (
                  <table
                    className="table table-striped table-bordered mb-4"
                    style={{ width: "70%", margin: "0 auto" }}
                  >
                    {delivery.header && (
                      <thead className="text-center">
                        <tr>
                          <th />
                          <th />
                          <th>{delivery.header}</th>
                          <th />
                        </tr>
                      </thead>
                    )}
                    <tbody className="text-center">
                      {delivery.tableData.map((item, i) => (
                        <tr key={i}>
                          <td className="text-start">{item.label}</td>
                          <td>{item.copies}</td>
                          <td>
                            Si{" "}
                            <input
                              type="radio"
                              {...register(item.field)}
                              value="1"
                              defaultChecked={
                                safeValue(master?.preve_save?.[item.field]) ===
                                "1"
                              }
                            />
                          </td>
                          <td>
                            No{" "}
                            <input
                              type="radio"
                              {...register(item.field)}
                              value="0"
                              defaultChecked={
                                safeValue(master?.preve_save?.[item.field]) ===
                                "0"
                              }
                            />
                          </td>
                          {item.hasNA && (
                            <td>
                              N/A{" "}
                              <input
                                type="radio"
                                {...register(item.field)}
                                value="2"
                                defaultChecked={
                                  safeValue(
                                    master?.preve_save?.[item.field],
                                  ) === "2"
                                }
                              />
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Botones */}
        <div className="row">
          <div className="col mt-4 d-flex justify-content-end gap-3">
            <button className="btnn" type="button" onClick={handleTicket}>
              Ticket
            </button>
            <button
              className="btnn"
              type="submit"
              disabled={
                isSubmitting ||
                (leftValidation && !leftValidation.isValid) ||
                (rightValidation && !rightValidation.isValid) ||
                flightSumErrors.col ||
                flightSumErrors.fro
              }
            >
              {isSubmitting ? "Guardando..." : "Guardar"}
            </button>
          </div>
        </div>
      </div>
    </form>
  );
}

export default ReceptionForm;
